import type { Term, Tone } from "@/lib/campaign";
import type { TriggerInput, TriggerUpdateInput } from "@/lib/queries";
import type { Trigger, TriggerInboxRow } from "@/lib/types";

/**
 * Triggers as the console shows and edits them (ADR-021): what each action does, where a request
 * stands and what it made, and the arithmetic between the form and core-api's `/triggers` routes.
 *
 * The rules are `platform`'s. The actions and the options each takes are `TRIGGER_ACTIONS` and
 * `_TRIGGER_OPTIONS`, a path is `trigger_mapping.PATH_RE`, the secret's header is
 * `_TRIGGER_HEADER_RE`, and what an enlist needs is `_enlist_problem` in core-api's `schemas.py`. The
 * server decides all of it. The copies here keep what it would refuse off the form and say why
 * first. Pure, for `test/triggers.test.ts`.
 */

// --- what a trigger does -------------------------------------------------------------------------

/** The order the form offers them in: what a zap needs most, first. */
export const TRIGGER_ACTIONS = [
  "lead.enlist", "lead.hold", "lead.release", "lead.delist", "lead.dnc", "capture",
] as const;

const ACTIONS: Record<string, Term> = {
  "lead.enlist": {
    label: "Add to a dial list",
    description: "Puts the lead on the campaign's dial list, and makes the lead first when the number is new: a lead from a form, a CRM or a zap.",
  },
  "lead.hold": {
    label: "Hold the lead",
    description: "Keeps the lead off the dial lists until an appointment ends, or for a set number of hours.",
  },
  "lead.release": {
    label: "End the hold",
    description: "Ends a hold now, so the dialer may call the lead again: a no-show, a cancellation.",
  },
  "lead.delist": {
    label: "Take off the dial lists",
    description: "Takes the lead off their dial lists now: signed, or not a case after all.",
  },
  "lead.dnc": { label: "Do not call", description: "Marks the lead do-not-call, on every campaign." },
  capture: {
    label: "Only keep what arrives",
    description: "Stores each request and does nothing else. Point a zap here first to see the fields it sends, then map them.",
  },
};

export function actionTerm(action: string): Term {
  return ACTIONS[action] ?? { label: action, description: "An action this console has no words for yet." };
}

/** The options each action takes: `_TRIGGER_OPTIONS`. */
export const ACTION_OPTIONS: Readonly<Record<string, readonly string[]>> = {
  capture: [],
  "lead.hold": ["hours", "reason", "timezone"],
  "lead.release": ["reason"],
  "lead.delist": [],
  "lead.dnc": [],
  "lead.enlist": ["consent_wording", "list_name", "timezone", "require_consent"],
};

/** The values a mapping can name, in the order the form asks for them. */
export const MAPPING_FIELDS = [
  "phone", "source_ref", "lead_id", "until", "first_name", "last_name", "language", "state", "timezone",
  "consent_at", "tags",
] as const;
export type MappingField = (typeof MAPPING_FIELDS)[number];

/** The fields that find a lead: `trigger_mapping.IDENTIFIERS`. */
export const IDENTIFIERS: readonly MappingField[] = ["lead_id", "source_ref", "phone"];

const ACTION_FIELDS: Readonly<Record<string, readonly MappingField[]>> = {
  capture: [],
  "lead.hold": [...IDENTIFIERS, "until"],
  "lead.release": IDENTIFIERS,
  "lead.delist": IDENTIFIERS,
  "lead.dnc": IDENTIFIERS,
  "lead.enlist": [
    ...IDENTIFIERS, "first_name", "last_name", "language", "state", "timezone", "consent_at", "tags",
  ],
};

/** The fields an action reads. A field only another action reads is not sent. */
export function actionFields(action: string): readonly MappingField[] {
  return ACTION_FIELDS[action] ?? IDENTIFIERS;
}

export const FIELD_WORDS: Record<MappingField, Term> = {
  phone: { label: "Phone number", description: "The lead's number, as a US number in any common format, or E.164." },
  source_ref: {
    label: "Sender's id",
    description: "The sending system's own id for the lead: a GHL contact id, a form entry's id.",
  },
  lead_id: { label: "Our lead id", description: "The platform's id for the lead, when the sender got it from a webhook." },
  until: {
    label: "Hold until",
    description: "When the hold ends, like the appointment's end: ISO 8601, epoch seconds, or MM/DD/YYYY with a time.",
  },
  first_name: { label: "First name", description: "" },
  last_name: { label: "Last name", description: "" },
  language: { label: "Language", description: "en or es, or the language's name: English, Spanish." },
  state: { label: "State", description: "The two-letter state, like CA." },
  timezone: {
    label: "Lead's time zone",
    description: "An IANA zone, like America/Los_Angeles. The lead's quiet hours are read in it.",
  },
  consent_at: { label: "Agreed at", description: "When the lead agreed to be called. Not in the future." },
  tags: {
    label: "Tags",
    description:
      "The sender's tags, as a list or as one comma-separated field. The firm, the language and the disposition are read from them.",
  },
};

// --- a request -----------------------------------------------------------------------------------

export const INBOX_STATUSES = ["received", "applied", "rejected", "failed"] as const;

const INBOX: Record<string, Term & { tone: Tone }> = {
  received: { label: "Waiting", description: "Stored, and applied within seconds.", tone: "neutral" },
  applied: { label: "Done", description: "The action ran.", tone: "success" },
  rejected: {
    label: "Not applied",
    description: "The action could not run on what the request carried, and says why.",
    tone: "warning",
  },
  failed: {
    label: "Failed",
    description: "The action raised an error five times. Send it again once the cause is fixed.",
    tone: "danger",
  },
};

export function inboxStatusText(status: string): Term & { tone: Tone } {
  return INBOX[status] ?? { label: status, description: "Not a status this console knows.", tone: "outline" };
}

/** Why a request was not applied: the `reason`s `trigger_actions.py` writes. */
const REASONS: Record<string, string> = {
  no_lead: "No lead has the number or id this request carries.",
  bad_until: "The end time could not be read.",
  no_phone: "No phone number for a new lead.",
  bad_phone: "The phone number is not one the platform can call.",
  no_source_ref: "No id from the sender for a new lead.",
  no_consent: "No moment of consent for a new lead.",
  bad_consent_date: "The moment of consent could not be read, or is in the future.",
  dnc: "The lead is on the do-not-call list.",
  no_campaign: "The trigger's campaign is gone.",
  unknown_action: "The trigger's action is not one the platform knows.",
};

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

function sentence(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/** What a request made, in one line. `when` writes the moment a hold ends. */
export function resultText(
  row: Pick<TriggerInboxRow, "status" | "result" | "error">,
  action: string,
  when: (iso: string) => string = (iso) => iso,
): string {
  const result: Record<string, unknown> = row.result ?? {};
  if (row.status === "received") return "Waiting to be applied.";
  if (row.status === "failed") return row.error ? `Failed: ${row.error}` : "Failed.";
  if (row.status === "rejected") {
    const reason = typeof result.reason === "string" ? result.reason : "";
    return REASONS[reason] ?? (reason ? `Not applied: ${reason}.` : "Not applied.");
  }
  const note = typeof result.note === "string" ? result.note : null;
  const count = (key: string) => (Array.isArray(result[key]) ? (result[key] as unknown[]).length : 0);
  switch (action) {
    case "capture":
      return "Kept, and nothing else.";
    case "lead.hold": {
      if (note) return sentence(note);
      const until = typeof result.hold_until === "string" ? when(result.hold_until) : "the end";
      return `Held on ${plural(count("campaign_leads"), "dial list")} until ${until}.`;
    }
    case "lead.release":
      return count("released") > 0
        ? `Ended the hold on ${plural(count("released"), "dial list")}.`
        : sentence(note ?? "nothing was held");
    case "lead.delist":
      return count("retired") > 0
        ? `Took the lead off ${plural(count("retired"), "dial list")}.`
        : sentence(note ?? "already off every dial list");
    case "lead.dnc":
      return result.dnc === "already" ? "Already do-not-call." : "Marked do-not-call.";
    case "lead.enlist":
      if (result.created === true) return "Made a new lead, and put them on the dial list.";
      return note ? sentence(note) : "Put a lead the platform had on the dial list.";
    default:
      return "Applied.";
  }
}

// --- the form ------------------------------------------------------------------------------------

export const DEFAULT_SECRET_HEADER = "X-ACL-Trigger-Secret";
/** `campaign_id: null`: every campaign of the tenant. */
export const EVERY_CAMPAIGN = "";
export const MAX_ATTRIBUTES = 30;
export const MAX_IDEMPOTENCY_PATHS = 5;
export const MAX_HOLD_HOURS = 24 * 90;

const STEP = String.raw`[A-Za-z0-9_\-]+(?:\[\d{1,4}\])?`;
/** A dotted path into a request, as `trigger_mapping.PATH_RE`. */
export const PATH_RE = new RegExp(String.raw`^(?:\$\.)?${STEP}(?:\.${STEP}){0,15}$`);
const HEADER_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,99}$/;
const RESERVED_HEADERS = new Set([
  "authorization", "connection", "content-length", "content-type", "cookie", "host", "idempotency-key",
  "transfer-encoding", "x-forwarded-for", "x-service-token",
]);
const REASON_RE = /^[a-z0-9][a-z0-9_]{0,49}$/;
const ATTRIBUTE_NAME_RE = /^[A-Za-z0-9_-]{1,64}$/;
const PLAIN_NAME_RE = /^[A-Za-z0-9_-]+$/;

export interface AttributeRow {
  name: string;
  path: string;
}

export interface TriggerValues {
  name: string;
  description: string;
  action: string;
  /** A campaign's id, or `EVERY_CAMPAIGN`. */
  campaign_id: string;
  lead_source: string;
  secret_header: string;
  enabled: boolean;
  /** A path per field; blank for a field the request does not carry. */
  mapping: Record<MappingField, string>;
  attributes: AttributeRow[];
  idempotency: string[];
  /** The options, as typed. */
  hours: string;
  reason: string;
  timezone: string;
  consent_wording: string;
  list_name: string;
  /** Off makes leads with no consent evidence, which the dialer will never call. */
  require_consent: boolean;
}

function blankMapping(): Record<MappingField, string> {
  return Object.fromEntries(MAPPING_FIELDS.map((field) => [field, ""])) as Record<MappingField, string>;
}

/** A new trigger starts as a zap's new-lead trigger, with its fields named the way "How to call it"
 *  tells a zap to name them. */
export const NEW_TRIGGER: TriggerValues = {
  name: "",
  description: "",
  action: "lead.enlist",
  campaign_id: EVERY_CAMPAIGN,
  lead_source: "zapier",
  secret_header: DEFAULT_SECRET_HEADER,
  enabled: true,
  mapping: {
    ...blankMapping(),
    phone: "phone",
    source_ref: "id",
    first_name: "first_name",
    last_name: "last_name",
    consent_at: "consented_at",
  },
  attributes: [],
  idempotency: [],
  hours: "",
  reason: "",
  timezone: "",
  consent_wording: "",
  list_name: "",
  require_consent: true,
};

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function triggerValues(trigger: Trigger): TriggerValues {
  const mapping = trigger.mapping ?? {};
  const options = trigger.options ?? {};
  const attributes = mapping.attributes;
  return {
    name: trigger.name,
    description: trigger.description ?? "",
    action: trigger.action,
    campaign_id: trigger.campaign_id ?? EVERY_CAMPAIGN,
    lead_source: trigger.lead_source ?? "",
    secret_header: trigger.secret_header,
    enabled: trigger.enabled,
    mapping: Object.fromEntries(MAPPING_FIELDS.map((field) => [field, text(mapping[field])])) as Record<
      MappingField,
      string
    >,
    attributes:
      attributes && typeof attributes === "object" && !Array.isArray(attributes)
        ? Object.entries(attributes).map(([name, path]) => ({ name, path: text(path) }))
        : [],
    idempotency: Array.isArray(mapping.idempotency) ? mapping.idempotency.map(text) : [],
    hours: typeof options.hours === "number" ? String(options.hours) : "",
    reason: text(options.reason),
    timezone: text(options.timezone),
    consent_wording: text(options.consent_wording),
    list_name: text(options.list_name),
    require_consent: options.require_consent !== false,
  };
}

function validZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** Why a trigger cannot be saved as it stands, or null: `trigger_settings_problem` and the field
 *  checks around it. */
export function triggerProblem(values: TriggerValues): string | null {
  if (!values.name.trim()) return "A trigger needs a name.";
  const header = values.secret_header.trim();
  if (!HEADER_RE.test(header)) return "The secret's header is a name of letters, digits and hyphens, up to 100.";
  if (RESERVED_HEADERS.has(header.toLowerCase())) {
    return `${header} already means something on a request; name another header.`;
  }
  const reads = actionFields(values.action);
  for (const field of reads) {
    const path = values.mapping[field].trim();
    if (path && !PATH_RE.test(path)) {
      return `${FIELD_WORDS[field].label}: "${path}" is not a path into the request. Write one like contact.phone.`;
    }
  }
  if (values.action === "lead.enlist") {
    const rows = values.attributes.filter((row) => row.name.trim() || row.path.trim());
    if (rows.length > MAX_ATTRIBUTES) return `A trigger fills at most ${MAX_ATTRIBUTES} attributes.`;
    const seen = new Set<string>();
    for (const row of rows) {
      if (!ATTRIBUTE_NAME_RE.test(row.name.trim())) {
        return "An attribute's name is letters, digits, _ and -, up to 64.";
      }
      if (!PATH_RE.test(row.path.trim())) return `The attribute ${row.name.trim()} needs a path into the request.`;
      if (seen.has(row.name.trim())) return `The attribute ${row.name.trim()} is named twice.`;
      seen.add(row.name.trim());
    }
  }
  const idempotency = values.idempotency.map((path) => path.trim()).filter(Boolean);
  if (idempotency.length > MAX_IDEMPOTENCY_PATHS) {
    return `A request is known by at most ${MAX_IDEMPOTENCY_PATHS} paths.`;
  }
  if (idempotency.some((path) => !PATH_RE.test(path))) {
    return "Each path a request is known by has to be a path into the request.";
  }
  const takes = ACTION_OPTIONS[values.action] ?? [];
  if (takes.includes("hours") && values.hours.trim()) {
    const hours = Number(values.hours);
    if (!Number.isInteger(hours) || hours < 1 || hours > MAX_HOLD_HOURS) {
      return `Hours is a whole number from 1 to ${MAX_HOLD_HOURS}.`;
    }
  }
  if (takes.includes("reason") && values.reason.trim() && !REASON_RE.test(values.reason.trim())) {
    return "A reason is lowercase letters, digits and underscores, up to 50.";
  }
  if (takes.includes("timezone") && values.timezone.trim() && !validZone(values.timezone.trim())) {
    return "A time zone is an IANA name, like America/Los_Angeles.";
  }
  if (values.action === "capture") return null;
  const mapped = (field: MappingField) => values.mapping[field].trim() !== "";
  if (mapped("source_ref") && !values.lead_source.trim()) {
    return "A sender's id needs the source its ids belong to.";
  }
  if (!IDENTIFIERS.some(mapped)) {
    return "Say where a request names the lead: its phone number, the sender's id, or our lead id.";
  }
  if (values.action === "lead.enlist") {
    if (values.campaign_id === EVERY_CAMPAIGN) return "Choose the campaign whose dial list the leads go on.";
    if (!mapped("phone")) return "Say where a request carries the phone number to call.";
    if (!mapped("source_ref")) {
      return "A new lead needs the sender's own id for them: say where a request carries it.";
    }
    if (values.require_consent) {
      if (!mapped("consent_at")) return "A new lead needs the moment they agreed: say where a request carries it.";
      if (!values.consent_wording.trim()) return "A new lead needs the words they agreed to.";
    }
  }
  return null;
}

/** What `POST /triggers` takes: the fields and options the action reads, and nothing it does not. */
export function triggerPayload(values: TriggerValues): Required<Omit<TriggerInput, "tenant_id">> {
  const mapping: Record<string, unknown> = {};
  for (const field of actionFields(values.action)) {
    const path = values.mapping[field].trim();
    if (path) mapping[field] = path;
  }
  if (values.action === "lead.enlist") {
    const attributes = Object.fromEntries(
      values.attributes
        .filter((row) => row.name.trim() || row.path.trim())
        .map((row) => [row.name.trim(), row.path.trim()]),
    );
    if (Object.keys(attributes).length > 0) mapping.attributes = attributes;
  }
  const idempotency = values.idempotency.map((path) => path.trim()).filter(Boolean);
  if (idempotency.length > 0) mapping.idempotency = idempotency;

  const takes = ACTION_OPTIONS[values.action] ?? [];
  const options: Record<string, unknown> = {};
  if (takes.includes("hours") && values.hours.trim()) options.hours = Number(values.hours);
  for (const name of ["reason", "timezone", "consent_wording", "list_name"] as const) {
    if (takes.includes(name) && values[name].trim()) options[name] = values[name].trim();
  }
  // Only when it is off: true is the server's own default, and an option it never had is tidier
  // than one that says what would happen anyway.
  if (takes.includes("require_consent") && !values.require_consent) options.require_consent = false;
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    action: values.action,
    mapping,
    options,
    campaign_id: values.campaign_id || null,
    lead_source: mapping.source_ref ? values.lead_source.trim() || null : null,
    secret_header: values.secret_header.trim(),
    enabled: values.enabled,
  };
}

/** JSON with every object's keys in order, to compare two mappings or options. */
function stable(value: unknown): string {
  return JSON.stringify(value, (_key, node: unknown) =>
    node && typeof node === "object" && !Array.isArray(node)
      ? Object.fromEntries(Object.entries(node).sort(([a], [b]) => a.localeCompare(b)))
      : node,
  );
}

/** The fields an edit changed, as `PUT /triggers/{id}` takes them. Mapping and options go whole. */
export function triggerChanges(values: TriggerValues, trigger: Trigger): TriggerUpdateInput {
  const next = triggerPayload(values);
  const out: TriggerUpdateInput = {};
  if (next.name !== trigger.name) out.name = next.name;
  if (next.description !== (trigger.description ?? null)) out.description = next.description;
  if (next.action !== trigger.action) out.action = next.action;
  if (stable(next.mapping) !== stable(trigger.mapping ?? {})) out.mapping = next.mapping;
  if (stable(next.options) !== stable(trigger.options ?? {})) out.options = next.options;
  if (next.campaign_id !== trigger.campaign_id) out.campaign_id = next.campaign_id;
  if (next.lead_source !== trigger.lead_source) out.lead_source = next.lead_source;
  if (next.secret_header !== trigger.secret_header) out.secret_header = next.secret_header;
  if (next.enabled !== trigger.enabled) out.enabled = next.enabled;
  return out;
}

// --- what a sender sends -------------------------------------------------------------------------

export interface RequestPath {
  path: string;
  /** The request's value there. */
  example: string;
}

/**
 * Every path a mapping can name in a request the trigger received, with the value there: into
 * objects, and into the first items of a list. A boolean is left out, because a mapping never reads
 * one, and so is a key no path can spell.
 */
export function requestPaths(doc: unknown): RequestPath[] {
  const out: RequestPath[] = [];
  const visit = (node: unknown, path: string) => {
    if (typeof node === "string" || typeof node === "number") {
      if (path) out.push({ path, example: String(node) });
    } else if (Array.isArray(node)) {
      node.slice(0, 5).forEach((item, index) => visit(item, `${path}[${index}]`));
    } else if (node !== null && typeof node === "object") {
      for (const [key, value] of Object.entries(node)) visit(value, path ? `${path}.${key}` : key);
    }
  };
  visit(doc, "");
  return out.filter((entry) => PATH_RE.test(entry.path));
}

const EXAMPLES: Record<MappingField, string> = {
  phone: "+13105550123",
  source_ref: "contact-123",
  lead_id: "the lead's id",
  until: "2026-09-20T18:00:00Z",
  first_name: "Rosa",
  last_name: "Delgado",
  language: "es",
  state: "CA",
  timezone: "America/Los_Angeles",
  consent_at: "2026-09-13T16:05:00Z",
  tags: "lhpark_ca,spanish",
};

/** Writes `value` at `path`, making the objects and lists on the way. */
function place(doc: Record<string, unknown>, path: string, value: unknown): void {
  const steps = path.replace(/^\$\./, "").split(".");
  let node: Record<string, unknown> = doc;
  steps.forEach((step, i) => {
    const last = i === steps.length - 1;
    const indexed = /^([A-Za-z0-9_-]+)\[(\d{1,4})\]$/.exec(step);
    if (indexed) {
      const name = indexed[1] ?? step;
      const index = Number(indexed[2]);
      const list = Array.isArray(node[name]) ? (node[name] as unknown[]) : [];
      node[name] = list;
      // A body shows every slot before the one a path names: a hole would read as nothing there.
      while (list.length < index) list.push(null);
      if (last) {
        list[index] = value;
        return;
      }
      const held = list[index];
      const next = held && typeof held === "object" && !Array.isArray(held) ? (held as Record<string, unknown>) : {};
      list[index] = next;
      node = next;
    } else if (last) {
      node[step] = value;
    } else {
      const held = node[step];
      const next = held && typeof held === "object" && !Array.isArray(held) ? (held as Record<string, unknown>) : {};
      node[step] = next;
      node = next;
    }
  });
}

function mappedPaths(trigger: Pick<Trigger, "action" | "mapping">): [string, unknown][] {
  const mapping = trigger.mapping ?? {};
  const out: [string, unknown][] = [];
  for (const field of actionFields(trigger.action)) {
    const path = mapping[field];
    if (typeof path === "string" && path) out.push([path, EXAMPLES[field]]);
  }
  const attributes = mapping.attributes;
  if (trigger.action === "lead.enlist" && attributes && typeof attributes === "object" && !Array.isArray(attributes)) {
    for (const [name, path] of Object.entries(attributes)) {
      if (typeof path === "string" && path) out.push([path, `the ${name}`]);
    }
  }
  if (Array.isArray(mapping.idempotency)) {
    mapping.idempotency.forEach((path, index) => {
      if (typeof path === "string" && path) out.push([path, `event-${index + 1}`]);
    });
  }
  return out;
}

/** A body shaped the way the trigger's mapping reads one, with example values: what "How to call it"
 *  shows a sender. */
export function exampleBody(trigger: Pick<Trigger, "action" | "mapping">): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [path, value] of mappedPaths(trigger)) place(body, path, value);
  return body;
}

/** The field names a form sends, when every path the trigger reads is a plain name; null when one
 *  steps into an object or a list, which only a JSON body carries. */
export function formFieldNames(trigger: Pick<Trigger, "action" | "mapping">): string[] | null {
  const paths = mappedPaths(trigger).map(([path]) => path.replace(/^\$\./, ""));
  return paths.every((path) => PLAIN_NAME_RE.test(path)) ? [...new Set(paths)] : null;
}
